import {Resolution} from "../../../common/resolution";
import {resolve} from "../../../container";
import {customElement, property, query} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import type {PopoverDisplayMode} from "./popoverDisplayMode";
import {AbsentDisplayMode, TooltipDisplayMode} from "./popoverDisplayMode";
import {templateContent} from "lit/directives/template-content.js";
import {ManagingResources} from "../../../common/lifetime";
import {PopoverAnchor} from "./popoverAnchor";
import {FeatureDetector} from "../../../common/featureDetector";
import type {Nullable} from "../../../bootstrap/common/types";
import {Dictionary} from "../dictionary";
import {trackingButtonInteract} from "../../../tracking/trackingAttributes";
import {UnLitElement} from "../../../common/elements";
import {unsafeSVG} from "lit/directives/unsafe-svg.js";
import {CLOSE_ICON} from "../../../common/svgIcons";

const POPOVER_BACKGROUNDS: Record<string, string> = {
    grayStronger: "popover-color-gray-stronger"
};

@customElement("eop-popover")
export class EopPopover extends ManagingResources(UnLitElement) {

    @query(".popover-container")
    public popoverContainer: HTMLElement;
    @query(".popover")
    public popoverElement: HTMLElement;
    @query(".close-button")
    public popoverCloser: HTMLElement;
    @property({attribute: "enforce-close-button", type: Boolean})
    public enforceCloseButton: boolean = false;
    @property({attribute: "content"})
    public contentId: string;

    @property({attribute: "background"})
    public background: string;

    @property({reflect: true, type: Boolean})
    public active: boolean = false;

    private dictionary: Dictionary;
    public managingParent?: PopoverAnchor;
    private displayMode: PopoverDisplayMode;

    public constructor(
        private features: FeatureDetector = resolve(FeatureDetector),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
        this.displayMode = new AbsentDisplayMode();
        this.resolution.onBootstrapBreakpointChange(() => this.adjustDisplayMode(), this);
    }

    private adjustDisplayMode(): void {
        if (!this.managingParent || !this.popoverContainer) {
            if (!(this.displayMode instanceof AbsentDisplayMode)) {
                this.displayMode.close();
                this.displayMode = new AbsentDisplayMode();
            }
        } else {
            if (!(this.displayMode instanceof TooltipDisplayMode)) {
                this.displayMode.close();
                this.displayMode = new TooltipDisplayMode(this);
            }
        }
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    public render(): TemplateResult {
        const template = this.resolveTemplateElement(this.contentId);
        return html`
            <span class="popover-container ${POPOVER_BACKGROUNDS[this.background]}">
                <span class="popover rich-text">
                    ${this.renderCloseButton()}
                    ${templateContent(template)}
                </span>
            </span>`;
    }

    public renderCloseButton(): Nullable<TemplateResult> {
        if (this.enforceCloseButton || this.features.isTouchDevice()) {
            return html`
                <button type="button"
                        class="close-button"
                        aria-label=${this.dictionary.translate("MSG_CLOSE_DIALOG")}
                        ${trackingButtonInteract("popover-close-button")}
                        @click=${() => this.close()}
                >${unsafeSVG(CLOSE_ICON)}</button>`;
        } else {
            return null;
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        this.adjustDisplayMode();
    }

    public onParent(parent: PopoverAnchor): void {
        this.managingParent = parent;
        this.adjustDisplayMode();
    }

    public silentlyShowAfter(action: () => void): void {
        this.active = true;
        action();
    }

    public doHide(): void {
        this.active = false;
    }

    public open(): void {
        this.displayMode.open();
    }

    public close(): void {
        this.displayMode.close();
    }

    public toggle(): void {
        this.displayMode.toggle();
    }
}